import { ref, computed, unref } from 'vue';

export function useElementStyles(settings, externalActive) {
	const isHovered = ref(false);
	const isActive = ref(false);

	function addPxIfNumber(value) {
		return isNaN(Number(value)) ? value : `${value}px`;
	}

	function isValidColor(color) {
		if (typeof color !== 'string') {
			return false;
		}

		const hexRegex = /^#([0-9A-Fa-f]{3}){1,2}$/;
		const rgbRegex = /^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/;
		const rgbaRegex = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(0(\.\d+)?|1(\.0+)?)\)$/;
		const transparentRegex = /^transparent$/;

		return (
			hexRegex.test(color) ||
			rgbRegex.test(color) ||
			rgbaRegex.test(color) ||
			transparentRegex.test(color)
		);
	}

	function getColorOrVariable(color) {
		if (isValidColor(color)) {
			return color;
		} else if (color) {
			return `var(--palette-${color})`;
		}
		return undefined;
	}

	const getStylesFromSettings = (s = {}) => {
		if (!s) return {};

		const {
			spacing = {},
			size = {},
			typography = {},
			background,
			backgroundImage,
			border = {},
			boxShadow = {},
		} = s;

		const raw = {
			padding: spacing.padding ? spacing.padding.map(p => `${p}px`).join(' ') : undefined,
			margin: spacing.margin ? spacing.margin.map(m => `${m}px`).join(' ') : undefined,

			width: addPxIfNumber(size.width),
			height: addPxIfNumber(size.height),
			minWidth: addPxIfNumber(size.minWidth),
			minHeight: addPxIfNumber(size.minHeight),
			maxWidth: addPxIfNumber(size.maxWidth),
			maxHeight: addPxIfNumber(size.maxHeight),

			fontFamily: typography.fontFamily,
			fontWeight: typography.fontWeight,
			fontSize: typography.fontSize ? `${typography.fontSize}px` : undefined,
			color: getColorOrVariable(typography.color),

			backgroundColor: getColorOrVariable(Array.isArray(background) ? background[0] : background),
			backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
			backgroundRepeat: backgroundImage ? 'no-repeat' : undefined,
			backgroundSize: backgroundImage ? 'cover' : undefined,

			borderWidth: border.borderWidth !== undefined ? `${border.borderWidth}px` : undefined,
			borderRadius: border.borderRadius !== undefined ? `${border.borderRadius}px` : undefined,
			borderColor: getColorOrVariable(border.color),
			borderStyle: border.borderWidth ? 'solid' : undefined,

			boxShadow: boxShadow.value
				? `${boxShadow.value.join('px ')}px ${getColorOrVariable(boxShadow.color) || 'transparent'}`
				: undefined,
		};

		return Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined));
	};

	const base = computed(() => getStylesFromSettings(unref(settings)));
	const hover = computed(() => getStylesFromSettings(unref(settings)?.interactions?.hover));
	const active = computed(() => getStylesFromSettings(unref(settings)?.interactions?.active));

	const styles = computed(() => {
		const isExternallyActive = unref(externalActive);

		if (isExternallyActive || isActive.value) {
			return { ...base.value, ...active.value };
		} else if (isHovered.value) {
			return { ...base.value, ...hover.value };
		} else {
			return base.value;
		}
	});

	return {
		styles,
		isHovered,
		isActive,
		getColorOrVariable
	};
}